// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import sharedActionCreators from "@modules/shared/actionCreator";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  LinearWizard,
  Step,
} from "@components";
import type { HierarchyAssignment } from "@modules/service/types";
import type { Rtw } from "@modules/service/reducer/rtw/types";

import formQuestionsSchema from "./form-questions-schema";
import RTWStepSettings from "./rtw-step-settings";
import StepCompliance from "./step-compliance";
import Step3Form from "./step-form";
import Step4Notifications from "./step-notifications";
import StepHierarchyScope from "./step-hierarchy-scope";

type OwnProps = $Exact<{
  defaultStep: 1 | 2 | 3 | 4 | 5,
  clientId: number,
  isActive: boolean,
  onCancel: () => void,
  schema: Object,
}>;

type PropsFromState = $Exact<{
  draft: ?Rtw,
  assignments: Array<HierarchyAssignment>,
  isAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  add: typeof actionCreators.AddRtw,
  resetState: typeof actionCreators.ResetRtwState,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class RtwAddModal extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    if (this.props.schema) {
      this.validate = new Ajv().compile(this.schema);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.validate = new Ajv().compile(this.schema);
    }
  }

  get schema() {
    const { schema } = this.props;
    if (!schema) {
      return null;
    }

    return {
      ...schema,
      properties: {
        ...schema.properties,
        formQuestions: formQuestionsSchema,
      },
    };
  }

  get isValid() {
    if (!this.validate && !this.props.schema) {
      return false;
    }

    const draft = this.props.draft?.content || {};
    const formData = {
      ...removeNullAndUndefinedProps(draft),
      formQuestions: draft.formQuestions, // removeNullAndUndefinedProps func removes nullable properties from questions and they do not get validated so we want to keep the original keys and values
    };

    const validate = this.validate || new Ajv().compile(this.schema);
    const isValid = validate(formData);
    if (process.env.NODE_ENV !== "production" && !isValid) {
      console.warn("isValid errors", validate.errors);
    }

    return isValid;
  }

  handleCancel = () => {
    this.props.onCancel();
  };

  handleSubmit = () => {
    this.props.add({
      clientId: this.props.clientId,
      isActive: this.props.isActive,
      draft: this.props.draft,
      assignments: this.props.assignments,
    });
    this.props.resetState();
    this.props.onCancel();
  };

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("RETURN_TO_WORK_ADD_TITLE")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          <LinearWizard defaultStep={this.props.defaultStep} data={[]}>
            <Step component={RTWStepSettings} title={this.props.t("RETURN_TO_WORK_STEP1")} />
            <Step component={StepCompliance} title={this.props.t("RETURN_TO_WORK_STEP2")} />
            <Step component={Step3Form} title={this.props.t("RETURN_TO_WORK_STEP3")} />
            <Step component={Step4Notifications} title={this.props.t("RETURN_TO_WORK_STEP4")} />
            <Step component={StepHierarchyScope} title={this.props.t("RETURN_TO_WORK_STEP5")} />
          </LinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            disabled={!(this.isValid && this.props.isAssignmentsValid)}
            onClick={this.handleSubmit}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    draft: serviceSelectors.getRtwDraft(state),
    assignments: serviceSelectors.getRtwAssignments(state),
    isAssignmentsValid: serviceSelectors.isRtwAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  add: actionCreators.AddRtw,
  resetState: actionCreators.ResetRtwState,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(RtwAddModal);
